import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import beneficiariesService, {
  type AddBeneficiaryPayload,
  type Beneficiary,
} from '../../services/beneficiaries.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { EMAIL_RE } from '../../lib/email';
import { colors } from '../../styles/theme';
import OnboardingRequiredPanel from '../../components/client/OnboardingRequiredPanel';
import { useClientApprovalGate } from '../../hooks/useCurrentUserProfile';

/**
 * The client's trusted contacts: people close to them whom QMI will recognise if they get in
 * touch about the client (owner, 2026-10-31; the idea of FINRA Rule 4512's "trusted contact
 * person").
 *
 * QMI keeps these details only to recognise these people if they get in touch about the client,
 * for example if something happens to them. A trusted contact has no access to the account and
 * no right over the money, and QMI does not contact them on its own initiative. It is NOT a
 * designation of who receives the client's money: a record in the portal has no legal effect on
 * a holding, which passes by will or succession (docs/design/deceased-holder-and-succession.md
 * §2.6). So the screen asks for no percentage, shows none, and says so plainly. The route and
 * the API keep their historical name (`beneficiaries`).
 *
 * Each trusted contact carries an e-mail, a phone or both (owner, 2026-10-31). The checks here
 * only catch obvious typos before a round-trip; the server validates and normalises both
 * (lower-case e-mail, E.164 phone) and its refusal is shown as it comes.
 *
 * Trusted contacts the client removed are listed below the current ones — never auto-hidden — so
 * the client sees the same trail the audit log keeps on the server.
 */

export const PAGE_TITLE = 'Trusted contacts';
// The owner's wording (2026-10-31). Held as strings rather than JSX text so it keeps its
// apostrophes exactly as written; the runtime translates them like any other text child.
const PAGE_SUBTITLE = "People close to you whom we'll recognise if they get in touch with us.";
export const NOTE_NO_ACCESS = "Your trusted contact can't see your account or make any transactions.";
export const NOTE_PURPOSE =
  'We keep their details only so we know who they are if they get in touch with us about you — for example, if something happens to you.';
export const NOTE_NOT_A_WILL =
  "Naming someone here doesn't leave them your money. If you pass away, your holdings go to the people named in your will or, if you have no will, to whoever the law determines.";

/** The most active trusted contacts a client may have; the server enforces the same number. */
export const MAX_ACTIVE_CONTACTS = 3;
/** The widths the API accepts (it validates both; these only stop absurd input). */
const EMAIL_MAX_LENGTH = 254;
const PHONE_MAX_LENGTH = 40;

export default function ClientBeneficiaries(): JSX.Element {
  const gate = useClientApprovalGate();
  const [rows, setRows] = useState<Beneficiary[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const data = await beneficiariesService.listMine();
      setRows(data);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(getApiErrorMessage(e, 'Could not load your trusted contacts.'));
    }
  }, []);

  useEffect(() => {
    // Gate the initial listing on onboarding approval; the gate panel
    // below stands in for the empty/loading view pre-approval.
    if (gate !== 'approved') return;
    void reload();
  }, [gate, reload]);

  const active = useMemo(() => (rows ?? []).filter((r) => r.is_active), [rows]);
  const inactive = useMemo(() => (rows ?? []).filter((r) => !r.is_active), [rows]);

  const shell = (body: JSX.Element): JSX.Element => (
    <div style={page}>
      <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
      {body}
    </div>
  );

  if (gate === 'loading') {
    return shell(<p className="text-sm" style={{ color: colors.muted }}>Loading…</p>);
  }

  if (gate === 'pending') {
    return shell(<OnboardingRequiredPanel feature="Trusted contacts" />);
  }

  const deactivate = async (b: Beneficiary): Promise<void> => {
    setActionErr(null);
    try {
      await beneficiariesService.deactivate(b.id);
      setFlash(`Removed ${b.full_name}.`);
      await reload();
    } catch (e) {
      setActionErr(getApiErrorMessage(e, 'Could not remove this trusted contact.'));
    }
  };

  return shell(
    <>
      <TrustedContactNote />

      {loadErr ? <div style={errBox}>{loadErr}</div> : null}
      {actionErr ? <div style={errBox}>{actionErr}</div> : null}
      {flash ? <div style={okBox}>{flash}</div> : null}

      <section style={card}>
        <h2 style={h2}>Your trusted contacts</h2>
        {/* A failed load says so in the red box above, never as an empty list. */}
        {rows === null && !loadErr && <p style={{ color: colors.muted }}>Loading…</p>}
        {rows !== null && <ContactsTable rows={active} onRemove={deactivate} />}
      </section>

      {rows === null ? null : (
        <AddContactsForm
          activeCount={active.length}
          onAdded={(msg) => {
            setActionErr(null);
            setFlash(msg);
            void reload();
          }}
          onError={setActionErr}
        />
      )}

      {inactive.length > 0 ? (
        <section style={card}>
          <h2 style={h2}>Previous trusted contacts</h2>
          <p style={{ fontSize: 12, color: colors.muted, marginTop: 0 }}>
            People you removed from your list. They are kept on record only.
          </p>
          <ContactsTable rows={inactive} />
        </section>
      ) : null}
    </>,
  );
}

/**
 * What a trusted contact is and is not, in the owner's words (2026-10-31): no access to the
 * account and no transactions; QMI keeps their details only to know who they are if they get in
 * touch about the client; naming them leaves them nothing, a holding passes by will or by law.
 */
export function TrustedContactNote(): JSX.Element {
  return (
    <aside style={noteBox} aria-label="About trusted contacts">
      <p style={{ margin: '0 0 6px 0' }}>
        <strong style={{ color: colors.ink }}>{NOTE_NO_ACCESS}</strong> {NOTE_PURPOSE}
      </p>
      <p style={{ margin: 0 }}>{NOTE_NOT_A_WILL}</p>
    </aside>
  );
}

interface ContactsTableProps {
  rows: readonly Beneficiary[];
  /** Present for the current trusted contacts; the previous ones are read-only. */
  onRemove?: (b: Beneficiary) => void;
}

/**
 * A list of trusted contacts: who they are, how they relate to the client, and their e-mail and
 * phone. Never a percentage. Each has an e-mail, a phone or both; the one missing shows a
 * dash.
 */
export function ContactsTable({ rows, onRemove }: Readonly<ContactsTableProps>): JSX.Element {
  if (rows.length === 0) {
    return (
      <p style={{ color: colors.muted, marginTop: 8 }}>
        You have not added any trusted contacts yet.
      </p>
    );
  }
  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={th}>Full name</th>
          <th style={th}>Relationship</th>
          <th style={th}>Email</th>
          <th style={th}>Phone</th>
          {onRemove ? <th style={th} aria-label="actions" /> : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((b) => (
          <tr key={b.id} style={onRemove ? undefined : { opacity: 0.6 }}>
            <td style={td}>{b.full_name}</td>
            <td style={td}>{b.relationship}</td>
            <td style={td}>{b.email ?? '—'}</td>
            <td style={td}>{b.phone ?? '—'}</td>
            {onRemove ? (
              <td style={td}>
                <button
                  type="button"
                  onClick={() => onRemove(b)}
                  style={btnGhost}
                  title="Remove this trusted contact"
                >
                  Remove
                </button>
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface AddProps {
  /** Count of currently-active trusted contacts (caps how many rows the form may add). */
  activeCount: number;
  onAdded: (message: string) => void;
  onError: (msg: string) => void;
}

export interface DraftRow {
  /** Stable client-side key so React can track rows across add/remove
   *  without falling back to the array index. */
  key: string;
  full_name: string;
  relationship: string;
  email: string;
  phone: string;
}

const blankRow = (): DraftRow => ({
  key: crypto.randomUUID(),
  full_name: '',
  relationship: '',
  email: '',
  phone: '',
});

// A number written with its country code (+, or the 00 / 011 dialling prefixes), 7 to 15 digits
// once spaces, dashes, dots and parentheses are dropped; any country. The server normalises it
// to E.164 and says why when it cannot. The sentences are the server's own (one Spanish each).
const COUNTRY_CODE = /^(?:\+|00|011)/;
const PHONE_RE = /^(?:\+|00|011)\d{7,15}$/;
const EMAIL_INVALID = 'Enter a valid email address for this trusted contact.';
const PHONE_INVALID =
  'Enter a valid phone number for this trusted contact, for example +52 55 1234 5678.';
const PHONE_COUNTRY_CODE = 'Include the country code, for example +52 or +1.';

/** Why the e-mail cannot be sent as typed, or null (an empty field is not an error). */
export function emailError(raw: string): string | null {
  const value = raw.trim();
  if (value === '' || EMAIL_RE.test(value)) return null;
  return EMAIL_INVALID;
}

/** Why the phone cannot be sent as typed, or null (an empty field is not an error): no country
 *  code, or a number that has one and still is not a number. */
export function phoneError(raw: string): string | null {
  const value = raw.trim();
  if (value === '') return null;
  const compact = value.replace(/[\s().-]/g, '');
  if (PHONE_RE.test(compact)) return null;
  return COUNTRY_CODE.test(compact) ? PHONE_INVALID : PHONE_COUNTRY_CODE;
}

/** A trusted contact needs an e-mail, a phone or both. */
export function reachError(row: Pick<DraftRow, 'email' | 'phone'>): string | null {
  return row.email.trim() === '' && row.phone.trim() === ''
    ? 'Add an email address or a phone number.'
    : null;
}

export function isRowValid(row: DraftRow): boolean {
  return (
    row.full_name.trim().length >= 3 &&
    row.relationship.trim().length >= 2 &&
    reachError(row) === null &&
    emailError(row.email) === null &&
    phoneError(row.phone) === null
  );
}

/** What the form sends: a name and a relationship per trusted contact, and the e-mail and phone the
 *  client filled in (an empty one is left out). Nothing else. */
export function toPayload(rows: readonly DraftRow[]): AddBeneficiaryPayload[] {
  return rows.map((r) => {
    const email = r.email.trim();
    const phone = r.phone.trim();
    return {
      full_name: r.full_name.trim(),
      relationship: r.relationship.trim(),
      ...(email === '' ? {} : { email }),
      ...(phone === '' ? {} : { phone }),
    };
  });
}

export function AddContactsForm({
  activeCount,
  onAdded,
  onError,
}: Readonly<AddProps>): JSX.Element {
  const [draftRows, setDraftRows] = useState<DraftRow[]>(() => [blankRow()]);
  const [submitting, setSubmitting] = useState(false);
  // Fields the client has left at least once (`<row key>:email`, `<row key>:phone`): an error
  // is shown only after that, never while they are still typing.
  const [visited, setVisited] = useState<ReadonlySet<string>>(() => new Set());
  const visit = (key: string): void => {
    setVisited((seen) => (seen.has(key) ? seen : new Set(seen).add(key)));
  };

  // How many more trusted contacts the client may add before reaching the cap.
  const slotsLeft = Math.max(0, MAX_ACTIVE_CONTACTS - activeCount);

  const canSubmit =
    !submitting &&
    draftRows.length >= 1 &&
    draftRows.length <= slotsLeft &&
    draftRows.every(isRowValid);

  const updateRow = (idx: number, patch: Partial<DraftRow>): void => {
    setDraftRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRow = (): void => {
    setDraftRows((rows) => (rows.length < slotsLeft ? [...rows, blankRow()] : rows));
  };
  const removeRow = (idx: number): void => {
    setDraftRows((rows) => (rows.length > 1 ? rows.filter((_, i) => i !== idx) : rows));
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await beneficiariesService.addBatchMine(toPayload(draftRows));
      const n = draftRows.length;
      onAdded(n === 1 ? `Added ${draftRows[0].full_name.trim()}.` : `Added ${n} trusted contacts.`);
      setDraftRows([blankRow()]);
    } catch (e) {
      onError(getApiErrorMessage(e, 'Could not save your trusted contacts.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (slotsLeft <= 0) {
    return (
      <section style={card}>
        <h2 style={h2}>Add a trusted contact</h2>
        <p style={{ color: colors.muted, marginTop: 0 }}>
          {`You already have the maximum of ${MAX_ACTIVE_CONTACTS} trusted contacts. Remove one before adding another.`}
        </p>
      </section>
    );
  }

  return (
    <section style={card}>
      <h2 style={h2}>Add trusted contacts</h2>
      <p style={{ fontSize: 12, color: colors.muted, marginTop: 0 }}>
        {`You can have up to ${MAX_ACTIVE_CONTACTS} trusted contacts; you can add ${slotsLeft} more.`}
      </p>
      <form onSubmit={submit} style={{ display: 'grid', gap: 16 }}>
        {draftRows.map((row, idx) => {
          const emailKey = `${row.key}:email`;
          const phoneKey = `${row.key}:phone`;
          const emailMsg = visited.has(emailKey) ? emailError(row.email) : null;
          const phoneMsg = visited.has(phoneKey) ? phoneError(row.phone) : null;
          const reachMsg =
            visited.has(emailKey) && visited.has(phoneKey) ? reachError(row) : null;
          return (
            <div key={row.key} style={rowCard}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 600, color: colors.muted }}>
                  {`Trusted contact ${idx + 1}`}
                </span>
                {draftRows.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    style={btnGhost}
                    title="Remove this row"
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                <label style={lbl}>
                  Full name
                  {/* */}
                  <input
                    type="text"
                    value={row.full_name}
                    onChange={(e) => updateRow(idx, { full_name: e.target.value })}
                    maxLength={200}
                    minLength={3}
                    required
                    style={inputStyle}
                    placeholder="As it appears on their ID"
                  />
                </label>
                <label style={lbl}>
                  Relationship
                  {/* */}
                  <input
                    type="text"
                    value={row.relationship}
                    onChange={(e) => updateRow(idx, { relationship: e.target.value })}
                    maxLength={100}
                    minLength={2}
                    required
                    style={inputStyle}
                    placeholder="Spouse, son, daughter, sibling, etc."
                  />
                </label>
                <p style={hint}>
                  Add an email address, a phone number or both. At least one is needed.
                </p>
                <label style={lbl}>
                  Email
                  {/* */}
                  <input
                    type="text"
                    inputMode="email"
                    autoComplete="email"
                    value={row.email}
                    onChange={(e) => updateRow(idx, { email: e.target.value })}
                    onBlur={() => visit(emailKey)}
                    maxLength={EMAIL_MAX_LENGTH}
                    aria-invalid={emailMsg !== null || reachMsg !== null}
                    style={inputStyle}
                    placeholder="name@example.com"
                  />
                  {emailMsg ? <span style={fieldErr}>{emailMsg}</span> : null}
                </label>
                <label style={lbl}>
                  Phone, with country code
                  {/* */}
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="off"
                    value={row.phone}
                    onChange={(e) => updateRow(idx, { phone: e.target.value })}
                    onBlur={() => visit(phoneKey)}
                    maxLength={PHONE_MAX_LENGTH}
                    aria-invalid={phoneMsg !== null || reachMsg !== null}
                    style={inputStyle}
                    placeholder="+52 55 1234 5678"
                  />
                  {phoneMsg ? <span style={fieldErr}>{phoneMsg}</span> : null}
                </label>
                {reachMsg ? <span style={fieldErr}>{reachMsg}</span> : null}
              </div>
            </div>
          );
        })}

        {draftRows.length < slotsLeft ? (
          <div>
            <button type="button" onClick={addRow} style={btnSecondary}>
              + Add another trusted contact
            </button>
          </div>
        ) : null}

        <div>
          <button type="submit" disabled={!canSubmit} style={btnPrimary}>
            {submitting ? 'Saving…' : 'Save trusted contacts'}
          </button>
        </div>
      </form>
    </section>
  );
}

const page: React.CSSProperties = {
  padding: 24,
  color: colors.ink,
  maxWidth: 760,
  margin: '0 auto',
};
const card: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: 12,
  padding: 16,
  marginTop: 16,
};
const noteBox: React.CSSProperties = {
  ...card,
  borderLeft: `4px solid ${colors.ink}`,
  marginTop: 0,
  marginBottom: 16,
  fontSize: 14,
  color: colors.inkSoft,
  lineHeight: 1.5,
};
const h2: React.CSSProperties = { margin: '0 0 12px 0', fontSize: 16, color: colors.ink };
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
  color: colors.ink,
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: `1px solid ${colors.borderSoft}`,
  color: colors.muted,
  fontSize: 12,
};
const td: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: `1px solid ${colors.borderSoft}`,
};
const lbl: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 13,
  color: colors.inkSoft,
};
const hint: React.CSSProperties = { margin: 0, fontSize: 12, color: colors.muted };
const fieldErr: React.CSSProperties = { fontSize: 12, color: colors.danger };
const inputStyle: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: colors.ink,
};
const btnPrimary: React.CSSProperties = {
  background: colors.ink,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 600,
};
const btnGhost: React.CSSProperties = {
  background: 'transparent',
  color: colors.danger,
  border: `1px solid ${colors.dangerBorder}`,
  borderRadius: 6,
  padding: '4px 10px',
  cursor: 'pointer',
  fontSize: 12,
};
const btnSecondary: React.CSSProperties = {
  background: 'transparent',
  color: colors.ink,
  border: `1px dashed ${colors.border}`,
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 600,
};
const rowCard: React.CSSProperties = {
  border: `1px solid ${colors.borderSoft}`,
  borderRadius: 10,
  padding: 12,
};
const errBox: React.CSSProperties = {
  background: colors.dangerBg,
  border: `1px solid ${colors.dangerBorder}`,
  color: colors.danger,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
};
const okBox: React.CSSProperties = {
  background: colors.successBg,
  border: `1px solid ${colors.successBorder}`,
  color: colors.success,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
};
